import React from "react";
import "./scorePanel.css";
import { IPuzzleV1 } from "../../dataLayer/v1/interfaces/classes/IPuzzleV1";
import { useObservableProperty } from "../../observable/useObservableProperty";
import { IWordInPuzzleV1 } from "../../dataLayer/v1/interfaces/classes/IWordInPuzzleV1";

export type ScorePanelProps = {
  puzzle: IPuzzleV1;
  timeRemainingInSeconds: number;
};

function pad(value: number): string {
  return value < 10 ? "0" + value.toString() : value.toString();
}

/** Found words, newest on the far left. When a word is added, existing chips
 *  slide (FLIP) to their new positions and the new chip fades in. */
function FoundWords({ words }: { words: string[] }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const prevLefts = React.useRef<Map<string, number>>(new Map());

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reduceMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const chips = Array.from(container.querySelectorAll<HTMLElement>(".word"));
    const newLefts = new Map<string, number>();
    chips.forEach((chip) => {
      const word = chip.dataset.word || "";
      newLefts.set(word, chip.getBoundingClientRect().left);
    });

    if (!reduceMotion) {
      chips.forEach((chip) => {
        const word = chip.dataset.word || "";
        const prev = prevLefts.current.get(word);
        const now = newLefts.get(word) as number;
        if (prev === undefined) {
          // New chip: fade/slide in from the left.
          chip.style.transition = "none";
          chip.style.transform = "translateX(-10px)";
          chip.style.opacity = "0";
          void chip.offsetWidth;
          chip.style.transition = "transform 0.28s ease, opacity 0.28s ease";
          chip.style.transform = "";
          chip.style.opacity = "1";
        } else if (Math.abs(prev - now) > 0.5) {
          // Existing chip moved: invert then play to slide to its new spot.
          const dx = prev - now;
          chip.style.transition = "none";
          chip.style.transform = `translateX(${dx}px)`;
          void chip.offsetWidth;
          chip.style.transition = "transform 0.28s cubic-bezier(0.2, 0.7, 0.2, 1)";
          chip.style.transform = "";
        }
      });
    }

    prevLefts.current = newLefts;
  }, [words]);

  return (
    <div className="play__found" ref={containerRef} aria-label="Words found">
      {words.map((w) => (
        <span className="word" data-word={w} key={w}>
          {w}
        </span>
      ))}
    </div>
  );
}

export function ScorePanel(props: ScorePanelProps) {
  const regularPoints = useObservableProperty<number | undefined>(props.puzzle.regularPoints);
  const wordsFound = useObservableProperty<IWordInPuzzleV1[]>(props.puzzle.foundWords);

  // Newest word on the far left; older words push right and fall off the edge
  // behind the fade. The left edge is anchored, so the list never shifts.
  const found = wordsFound
    .slice()
    .reverse()
    .map((w) => w.word.toLowerCase());
  const minutes = Math.floor(props.timeRemainingInSeconds / 60);
  const seconds = Math.floor(props.timeRemainingInSeconds % 60);

  return (
    <>
      <div className="play__scores">
        <div className="stat stat--time">
          <div className="stat__label">Time</div>
          <div className="stat__val num">
            {minutes}:{pad(seconds)}
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">Words</div>
          <div className="stat__val num">{wordsFound.length}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Points</div>
          <div className="stat__val num">{(regularPoints ?? 0).toLocaleString()}</div>
        </div>
      </div>

      <FoundWords words={found} />
    </>
  );
}
